import { withNamespaces } from "react-i18next";
import { Alert, Button, Col, Label, Row, Spinner } from "reactstrap";
import { useEffect, useRef, useState } from "react";
import MailerService from "src/modules/3rd-party-management/apis/admin/MailerService";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { AvField, AvForm } from "availity-reactstrap-validation";
import ToggleActivationModal from "./components/ToggleActivationModal";

const EmailSettings = (props) => {
    const { t } = props;
    const formRef = useRef();
    const [initialSettings, setInitialSettings] = useState();
    const [defaultSettings, setDefaultSettings] = useState({});
    const [testSuccess, setTestSuccess] = useState(null);
    const [active, setActive] = useState(false);
    const [showToggleActivationModal, setShowToggleActivationModal] = useState(false);
    const service = MailerService.getInstance();

    const {
        isFetching: isFetchingSettings
    } = useQuery({
        queryKey: ["3rd-party-fetch-mailer-settings"],
        queryFn: async () => {
            return await service.fetchMailerSettings();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onSuccess: (data) => {
            setInitialSettings(data);
            setDefaultSettings(data);
            setActive(data?.isActive);
        },
        onError: () => {
            toast(props.t("An error occurred while fetching settings."), {
                type: "error"
            });
        },
    });

    const submitMutation = useMutation({
        mutationFn: async (payload) => {
            return await service.saveMailerSettings(payload);
        },
        onSuccess: () => {
            toast.success(t("Settings saved successfully."));
            if(!active) {
                setShowToggleActivationModal('activation_after_save');
            }
        },
        onError: () => {
            toast.error(t("An error occurred while saving settings."));
        },
    });

    const toggleActivationMutation = useMutation({
        mutationFn: async (payload) => {
            return await service.toggleMailerActivation(payload);
        },
        onSuccess: () => {
            toast.success(t(`Settings ${active ? 'deactivated' : 'activated'} successfully.`));
            setActive(!active);
        },
        onError: () => {
            toast.error(t("An error occurred while toggling activation."));
        },
    });

    const testConnectionMutation = useMutation({
        mutationFn: async (payload) => {
            return await service.testConnection(payload);
        },
        onSuccess: () => {
            toast.success(t('Connection successful. Emails can be sent with this configuration.'));
            setTestSuccess(true);
        },
        onError: () => {
            toast.error(t("Connection failed. Please check your SMTP settings."));
            setTestSuccess(false);
        }
    });

    const handleSubmit = (_e, values) => {
        submitMutation.mutate(values);
    };

    const handleTestConnection = () => {
        testConnectionMutation.mutate(formRef.current.getValues());
    }

    const handleRestore = () => {
        setDefaultSettings(null)
    };

    useEffect(() => {
        if(!defaultSettings && initialSettings) {
            setDefaultSettings(initialSettings);
        }
    }, [ defaultSettings, initialSettings ]);

    const isFirstTime = !initialSettings?.emailAddress && 
                        !initialSettings?.smtpServer && 
                        !initialSettings?.smtpUsername;

    return (
        <Row>
            <Col sm="12" md='12' xl="9">
                {!isFirstTime && (
                    <Alert 
                        color={active ? 'success' : 'warning'} 
                        className="mb-4"
                    >
                        {active ? (
                            <>
                                <i className="ri-checkbox-circle-fill me-1 font-size-22 align-middle" /> 
                                {t('Email settings are active. The system is sending emails.')}
                            </>
                        ) : (
                            <>
                                <i className="ri-error-warning-fill me-1 font-size-22 align-middle" />
                                {t('Email settings are currently disabled. Activate them to allow the system to send emails.')}
                            </>
                        )}
                    </Alert>
                )}

                <AvForm
                    className="needs-validation"
                    onValidSubmit={handleSubmit}
                    ref={formRef}
                >
                    <Row>
                        <Col sm="12" md="6" className="mb-3">
                            <Label for="emailAddress">
                                {t("From Email Address")}
                            </Label>
                            {isFetchingSettings ? (
                                <div 
                                    className="dt-field dt-skeleton" 
                                    style={{width: '100%'}} 
                                />
                            ) : (
                                <AvField
                                    id="emailAddress"
                                    name="emailAddress"
                                    type="email"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: {
                                            value: true,
                                            errorMessage: t("This field cannot be blank"),
                                        },
                                        email: { errorMessage: t("Your email is invalid") },
                                    }}
                                    value={defaultSettings?.emailAddress || ""}
                                />
                            )}
                        </Col>

                        <Col sm="12" md="6" className="mb-3">
                            <Label for="displayName">
                                {t("Sender Name")}
                            </Label>
                            {isFetchingSettings ? (
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: '100%' }}
                                />
                            ) : (
                                <AvField
                                    id="displayName"
                                    name="displayName"
                                    type="text"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true }
                                    }}
                                    value={defaultSettings?.displayName || ""}
                                />
                            )}
                        </Col>

                        <Col sm="12" md="6" className="mb-3">
                            <Label for="smtpServer">
                                {t("SMTP Server")}
                            </Label>
                            {isFetchingSettings ? (
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: '100%' }}
                                />
                            ) : (
                                <AvField
                                    id="smtpServer"
                                    name="smtpServer"
                                    type="text"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true }
                                    }}
                                    value={defaultSettings?.smtpServer || ""}
                                />
                            )}
                        </Col>

                        <Col sm="12" md="6" className="mb-3">
                            <Label for="smtpPort">
                                {t("SMTP Port")}
                            </Label>
                            {isFetchingSettings ? (
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: '100%' }}
                                />
                            ) : (
                                <AvField
                                    id="smtpPort"
                                    name="smtpPort"
                                    type="number"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true }
                                    }}
                                    value={defaultSettings?.smtpPort || ""}
                                />
                            )}
                        </Col>

                        <Col sm="12" md="6" className="mb-3">
                            <Label for="smtpUsername">
                                {t("SMTP Username")}
                            </Label>
                            {isFetchingSettings ? (
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: '100%' }}
                                />
                            ) : (
                                <AvField
                                    id="smtpUsername"
                                    name="smtpUsername"
                                    type="text"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true }
                                    }}
                                    value={defaultSettings?.smtpUsername || ""}
                                />
                            )}
                        </Col>

                        <Col sm="12" md="6" className="mb-3">
                            <Label for="authModeOut">
                                {t("Authentication Mode")}
                            </Label>
                            {isFetchingSettings ? (
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: '100%' }}
                                />
                            ) : (
                                <AvField
                                    id="authModeOut"
                                    name="authModeOut"
                                    type="select"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true }
                                    }}
                                    value={defaultSettings?.authModeOut || ""}
                                >
                                    <option value="">{t("Select Authentication Mode")}</option>
                                    <option value="PASSWORD">{t('Password')}</option>
                                    <option value="APP_PASSWORD">{t('App Password')}</option>
                                </AvField>
                            )}
                        </Col>

                        <Col sm="12" md="6" className="mb-3">
                            <Label for="smtpSecret">
                                {t("SMTP Password / App Password")}
                            </Label>
                            {isFetchingSettings ? (
                                <div
                                    className="dt-field dt-skeleton"
                                    style={{ width: '100%' }}
                                />
                            ) : (
                                <AvField
                                    id="smtpSecret"
                                    name="smtpSecret"
                                    type="password"
                                    errorMessage={t("This field cannot be blank")}
                                    className="form-control"
                                    validate={{
                                        required: { value: true }
                                    }}
                                />
                            )}
                        </Col>

                        <Col sm="12" className="text-end">
                            <div className="d-inline-block">
                                <div>
                                    <Button
                                        color="primary"
                                        type="submit"
                                        disabled={isFetchingSettings || submitMutation.isLoading}
                                        className="me-2"
                                    >
                                        {submitMutation.isLoading && (
                                            <Spinner
                                                size="sm"
                                                color="priamry"
                                                className="me-2"
                                            />
                                        )}
                                        {t("Save Settings")}
                                    </Button>

                                    {!isFirstTime && (
                                        <>
                                            <Button
                                                type="button"
                                                color="info"
                                                className="me-2"
                                                onClick={handleTestConnection}
                                                disabled={isFetchingSettings || submitMutation.isLoading || testConnectionMutation.isLoading}
                                            >
                                                {testConnectionMutation.isLoading && (
                                                    <Spinner
                                                        size="sm"
                                                        color="white"
                                                        className="me-2"
                                                    />
                                                )}
                                                {t("Test Connection")}
                                            </Button>

                                            <Button
                                                type="button"
                                                color={active ? 'danger' : 'success'}
                                                className="me-2"
                                                onClick={() => setShowToggleActivationModal(active ? 'deactivate' : 'activate')}
                                                disabled={isFetchingSettings || submitMutation.isLoading}
                                            >
                                                {active ? (
                                                    t("Deactivate Email Settings")
                                                ) : (
                                                    t("Activate Email Settings")
                                                )}
                                            </Button>
                                        </>
                                    )}

                                    <Button
                                        color="secondary"
                                        type="button"
                                        outline
                                        onClick={handleRestore}
                                        disabled={isFetchingSettings || submitMutation.isLoading}
                                    >
                                        {t("Restore Defaults")}
                                    </Button>
                                </div>
                                <div className="text-start mt-1">
                                    {testSuccess === true && (
                                        <span className="text-success">
                                            <i className="ri-check-line align-middle font-size-18"></i>
                                            {t("Connection successful. Emails can be sent.")}
                                        </span>
                                    )}
                                    {testSuccess === false && (
                                        <span className="text-danger">
                                            <i className="ri-error-warning-line align-middle font-size-18"></i>
                                            {t("Connection failed: invalid credentials.")}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                </AvForm>

                <ToggleActivationModal 
                    isOpen={showToggleActivationModal !== false}
                    toggle={() => {
                        setShowToggleActivationModal(false)
                    }}
                    toggleActivationMutation={toggleActivationMutation}
                    action={showToggleActivationModal}
                />
            </Col>
        </Row>
    );
};

export default withNamespaces()(EmailSettings);
